//
// Module-level loading registry for coordinating initial app load.
//
// Loaders register at module load time (before React renders).
// They complete/suspend/resume during React lifecycle.
// LoadingGate uses this to show a unified progress indicator.
//

// Loader status: 'pending' | 'complete' | 'suspended'
const loaders = new Map();
const listeners = new Set();

let nextId = 1;

/**
 * Register a loader at module load time.
 * Call this at the top level of your module, outside any component.
 *
 * @param {string} label - Human-readable label for debugging
 * @returns {number} Loader ID to use with other functions
 */
export function registerLoader(label) {
  const id = nextId;
  nextId += 1;
  loaders.set(id, { label, status: 'pending' });
  // Note: no notify here - this runs before React, no listeners yet
  return id;
}

/**
 * Get current loading progress.
 *
 * @returns {{
 *    total: number,
 *    done: number,
 *    progress: number,
 *    allComplete: boolean,
 *    loaders: Array
 * }}
 */
export function getProgress() {
  const all = [...loaders.values()];
  const active = all.filter((l) => (l.status !== 'suspended'));
  const completed = active.filter((l) => (l.status === 'complete'));

  return {
    total: active.length,
    done: completed.length,
    progress: active.length ? (completed.length / active.length) : 1,
    allComplete: (completed.length >= active.length) && (active.length > 0),
    // For debugging
    loaders: all.map((l) => ({ label: l.label, status: l.status })),
  };
}

function notifyListeners() {
  const progress = getProgress();
  listeners.forEach((fn) => fn(progress));
}

/**
 * Mark a loader as complete. Called when resource has loaded.
 *
 * @param {number} id - Loader ID from registerLoader
 */
export function completeLoader(id) {
  const loader = loaders.get(id);
  if (loader && (loader.status !== 'complete')) {
    loader.status = 'complete';
    notifyListeners();
  }
}

/**
 * Suspend a loader. Called when loader determines it cannot/should not load
 * (e.g., user is logged out, so config cannot be fetched).
 * Suspended loaders don't count toward progress.
 *
 * @param {number} id - Loader ID from registerLoader
 */
export function suspendLoader(id) {
  const loader = loaders.get(id);
  if (loader && (loader.status !== 'suspended')) {
    loader.status = 'suspended';
    notifyListeners();
  }
}

/**
 * Resume a previously suspended loader. Called when conditions change
 * and loader should start/continue loading (e.g., user logged in).
 * Resets status to 'pending'.
 *
 * @param {number} id - Loader ID from registerLoader
 */
export function resumeLoader(id) {
  const loader = loaders.get(id);
  if (loader && (loader.status === 'suspended')) {
    loader.status = 'pending';
    notifyListeners();
  }
}

/**
 * Subscribe to progress changes.
 *
 * @param {Function} fn - Callback receiving progress object
 * @returns {Function} Unsubscribe function
 */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Reset all loaders to pending state. Useful for testing or full app reset.
 */
// export function resetLoaders() {
//   loaders.forEach((loader) => {
//     loader.status = 'pending';
//   });
//   notifyListeners();
// }
